import type { WidenMessageLeaves } from '../types';

export const enCoreMessages = {
  common: {
    back: 'Back',
    or: 'or',
    emailAddress: 'Email address',
  },
  navigation: {
    home: 'Home',
    login: 'Login',
    register: 'Register',
    dashboard: 'Home',
  },
  googleAuth: {
    blockedAriaLabel: 'Complete required fields first',
    disabled: 'Google login disabled. Set `NEXT_PUBLIC_GOOGLE_CLIENT_ID` to enable social login.',
    failed: 'Google login failed',
    finishing: 'Finishing Google login...',
    tokenMissing: 'Google login token was not returned.',
    unavailable: 'Google login unavailable',
    // Native app only (stores project, batch 3): system Apple and Google buttons.
    appleCta: 'Continue with Apple',
    googleCta: 'Continue with Google',
    appleFailed: 'Apple sign-in failed. Try again in a moment.',
    googleNativeFailed: 'Google sign-in failed. Try again in a moment.',
    finishingNative: 'Finishing sign-in...',
  },
  account: {
    premium: {
      title: 'Premium',
      activeLabel: 'Premium active',
      manage: 'Manage subscription',
      packActiveLabel: 'Moving pack active until {date}',
      freeDescription: 'Unlimited scans and shop, or a 6-month Moving pack.',
      upgrade: 'Go Premium',
      manageStore: 'Manage or cancel',
      restore: 'Restore purchases',
    },
    title: 'Settings',
    profile: 'Profile',
    authenticatedDescription: 'Your account keeps your inventory and future app settings synced.',
    avatar: {
      add: 'Add a photo',
      change: 'Change photo',
      remove: 'Remove',
      uploading: 'Uploading...',
      error: 'The photo could not be saved. Please try again.',
    },
    changePassword: 'Change password',
    logout: 'Log out',
    guestTitle: 'Guest session',
    guestDescription:
      'You can use Liberio as a guest, but creating an account enables to save your preferences and inventory.',
    register: 'Register',
    login: 'Log in',
    zone: {
      title: 'My area',
      description: 'To recommend the best options near you.',
      placeholder: 'New postal code',
      checking: 'Checking...',
      unknown: 'Postal code not found. Check the 5 digits.',
      update: 'Update',
      saving: 'Saving...',
    },
    boutiqueMode: {
      title: 'Your Liberio version',
      activeLabel: 'Shop version',
      activeDescription:
        'Scan your items and share your shop, wherever you are. Destinations and action plans only exist in France.',
      inactiveLabel: 'Full app (France)',
      inactiveDescription: 'Destinations, action plans and shop: the version designed for France.',
      switchToFull: 'I am in France: switch to the full app',
      switchToBoutique: 'Switch back to the shop version',
      error: 'The change was not saved. Please try again.',
    },
    languageTitle: 'Language',
    legal: {
      title: 'Legal',
      terms: 'Terms of Use and Sale',
      privacy: 'Privacy policy',
      notices: 'Legal notice',
    },
    feedbackTitle: 'Feedback',
    feedbackSubmitted:
      'You already shared feedback for the current version. You can reopen it any time to add more or update it.',
    feedbackSkipped:
      'You skipped the current feedback request, but you can still send feedback any time from here.',
    feedbackDefault: 'Share product feedback any time. Your latest response updates the current feedback version.',
    feedbackCtaExisting: 'Give more feedback',
    feedbackCtaNew: 'Open feedback',
    settings: {
      groups: {
        preferences: 'My preferences',
        account: 'My account',
        help: 'Help and feedback',
      },
      freePlan: 'Free account',
      promoSubtitle: 'Unlimited scans and shop',
      promoCta: 'Discover',
      reminders: {
        bannerTitle: 'Turn on reminders',
        bannerSubtitle: 'So you never forget',
        bannerCta: 'Turn on',
        dismiss: 'Close',
        row: 'Reminders',
        on: 'On',
        off: 'Off',
        blocked: 'Blocked',
      },
      name: {
        edit: 'Edit my first name',
        placeholder: 'Your first name',
        save: 'Save',
        saving: 'Saving...',
        error: 'Your first name could not be saved. Try again.',
      },
      photo: 'Change my photo',
      whatsApp: 'Help on WhatsApp',
      legalShort: {
        terms: 'Terms',
        privacy: 'Privacy',
      },
    },
    whatsAppHelp: {
      title: 'Need help?',
      description: 'Message us on WhatsApp and tell us what you are trying to do with your objects.',
      cta: 'Ask on WhatsApp',
      prefill: 'Hi, I am using Liberio and I would like some help.',
    },
    install: {
      title: 'Install Liberio',
      description: 'Open Liberio from your home screen like an app.',
      cta: 'See how',
      directCta: 'Install',
    },
    notifications: {
      title: 'Notifications',
      description: 'Enable reminders for your next object actions, saved plans, and useful Liberio updates.',
      cta: 'Enable notifications',
      deniedTitle: 'Notifications are blocked',
      deniedDescriptionIos: 'On iPhone: Settings > Apps > Liberio > Notifications, then turn on Allow Notifications.',
      deniedDescriptionGeneric: 'Notifications are blocked for Liberio. Re-enable them in your device or browser notification settings.',
    },
    deleteAccount: {
      title: 'Delete my account',
      description:
        'You can delete your account at any time. This is permanent: your inventory, scans, photos and personal data will be erased.',
      cta: 'Delete my account',
      confirmTitle: 'Delete your account?',
      confirmDescription: 'This is permanent. The following will be erased:',
      itemInventory: 'your inventory and scans',
      itemPhotos: 'your photos',
      itemProgress: 'your progress and logbook',
      itemPersonal: 'your email address and preferences',
      premiumNote: 'Your Premium subscription will be cancelled immediately.',
      storeSubscriptionNoteIos:
        'Your Premium subscription bought on the App Store does not stop with your account. Cancel it in your iPhone settings to stop being billed.',
      storeSubscriptionNoteAndroid:
        'Your Premium subscription bought on Google Play does not stop with your account. Cancel it in Google Play to stop being billed.',
      typeToConfirm: 'To confirm, type DELETE:',
      confirmKeyword: 'DELETE',
      confirmCta: 'Delete permanently',
      deleting: 'Deleting...',
      cancel: 'Cancel',
      error: 'Deletion failed. Your account was not deleted, try again in a moment.',
    },
    languageLabels: {
      en: 'English',
      fr: 'Français',
      es: 'Español',
    },
  },
  notificationsSoftAsk: {
    title: 'Liberio works better with notifications',
    description: 'Turn them on to enjoy every feature: reminders, challenges, tracking…',
    activate: 'Turn on notifications',
    later: 'Not now',
  },
  locationSoftAsk: {
    intro: 'Turn on your location to see drop-off and resale points around you.',
    activate: 'Turn on my location',
    usePostalCode: 'Enter my postcode',
    postalIntro:
      'To get the most out of Liberio, we need at least your postcode so we can point you to local businesses.',
    postalLabel: 'Postcode',
    postalPlaceholder: '75011',
    postalUnknown: 'Unknown postcode',
    checking: 'Searching…',
    submit: 'Continue',
    onlineOnly: "I'm only interested in online options",
  },
  formErrorBanner: {
    title: "Couldn't continue",
  },
  actionGuidance: {
    effort: {
      minimal: 'Effort: minimal',
      low: 'Effort: low',
      medium: 'Effort: medium',
      high: 'Effort: high',
    },
    money: {
      none: 'Money: none',
      low: 'Money: low',
      medium: 'Money: medium',
      high: 'Money: high',
      highest: 'Money: highest',
    },
    meter: {
      effort: 'Effort',
      money: 'Money',
    },
  },
  listingSummary: {
    selectedItem: 'Selected item',
    selectedItemAlt: 'Selected item',
    item: 'Item',
    untitledItem: 'Untitled item',
  },
  taxonomy: {
    conditions: {
      like_new: 'Like new',
      good: 'Good general state',
      fair: 'Used',
      poor: 'Poor',
    },
    labels: {
      electronics: 'Electronics',
      smartphone: 'Smartphone',
      'feature-phone': 'Feature Phone',
      tablet: 'Tablet',
      smartwatch: 'Smartwatch',
      'headphones-audio': 'Headphones & Audio',
      camera: 'Camera',
      television: 'Television',
      'gaming-console': 'Gaming Console',
      computers: 'Computers',
      laptop: 'Laptop',
      'desktop-computer': 'Desktop Computer',
      monitor: 'Monitor',
      'pc-component': 'PC Component',
      'networking-gear': 'Networking Gear',
      'printer-scanner': 'Printer & Scanner',
      'computer-accessory': 'Computer Accessory',
      'home-appliances': 'Home Appliances',
      'small-kitchen-appliance': 'Small Kitchen Appliance',
      'large-kitchen-appliance': 'Large Kitchen Appliance',
      'cleaning-appliance': 'Cleaning Appliance',
      'climate-control-appliance': 'Climate Control Appliance',
      'personal-care-appliance': 'Personal Care Appliance',
      furniture: 'Furniture',
      sofa: 'Sofa',
      table: 'Table',
      chair: 'Chair',
      'storage-furniture': 'Storage Furniture',
      'bedroom-furniture': 'Bedroom Furniture',
      'office-furniture': 'Office Furniture',
      'outdoor-furniture': 'Outdoor Furniture',
      'home-decor': 'Home & Decor',
      lighting: 'Lighting',
      'rug-textile': 'Rug & Textile',
      'wall-art': 'Wall Art',
      kitchenware: 'Kitchenware',
      'garden-decor': 'Garden Decor',
      fashion: 'Fashion',
      'womens-clothing': "Women's Clothing",
      'mens-clothing': "Men's Clothing",
      footwear: 'Footwear',
      bag: 'Bag',
      'watch-jewelry': 'Watch & Jewelry',
      'fashion-accessory': 'Fashion Accessory',
      'baby-kids': 'Baby & Kids',
      stroller: 'Stroller',
      'car-seat': 'Car Seat',
      'baby-gear': 'Baby Gear',
      'kids-clothing': 'Kids Clothing',
      'learning-toy': 'Learning Toy',
      'maternity-item': 'Maternity Item',
      'sports-outdoors': 'Sports & Outdoors',
      'fitness-gear': 'Fitness Gear',
      bicycle: 'Bicycle',
      'standard-bike': 'Standard Bike',
      'e-bike': 'E-Bike',
      'running-gear': 'Running Gear',
      'team-sport-gear': 'Team Sport Gear',
      'camping-hiking-gear': 'Camping & Hiking Gear',
      'water-sport-gear': 'Water Sport Gear',
      'tools-diy': 'Tools & DIY',
      'power-tool': 'Power Tool',
      'hand-tool': 'Hand Tool',
      'workshop-equipment': 'Workshop Equipment',
      'construction-material': 'Construction Material',
      'gardening-tool': 'Gardening Tool',
      'auto-moto': 'Auto & Moto',
      'car-part': 'Car Part',
      'moto-part': 'Moto Part',
      'wheel-tire': 'Wheel & Tire',
      'car-electronic': 'Car Electronic',
      'vehicle-accessory': 'Vehicle Accessory',
      'books-media': 'Books & Media',
      book: 'Book',
      'comic-magazine': 'Comic & Magazine',
      'movie-disc': 'DVD & Blu-ray',
      dvd: 'DVD',
      'blu-ray': 'Blu-ray',
      'music-recording': 'CDs & vinyl',
      cd: 'CD',
      vinyl: 'Vinyl',
      'games-toys': 'Games & Toys',
      'video-game': 'Video Game',
      'board-game': 'Board Game',
      puzzle: 'Puzzle',
      'action-figure': 'Action Figure',
      'educational-toy': 'Educational Toy',
      'collectibles-art': 'Collectibles & Art',
      antique: 'Antique',
      'coin-stamp': 'Coin & Stamp',
      'trading-card': 'Trading Card',
      artwork: 'Artwork',
      'vintage-item': 'Vintage Item',
      'musical-instruments': 'Musical Instruments',
      'guitar-bass': 'Guitar & Bass',
      'keyboard-piano': 'Keyboard & Piano',
      'drum-percussion': 'Drum & Percussion',
      'dj-audio-gear': 'DJ & Audio Gear',
      'studio-recording-gear': 'Studio Recording Gear',
      'wind-string-instrument': 'Wind & String Instrument',
      'health-beauty': 'Health & Beauty',
      'skincare-item': 'Skincare Item',
      'makeup-item': 'Makeup Item',
      'haircare-item': 'Haircare Item',
      'wellness-device': 'Wellness Device',
      'medical-equipment': 'Medical Equipment',
      pets: 'Pets',
      'pet-supplies': 'Pet Supplies',
      plants: 'Plants',
      'live-plant': 'Live Plant',
      iphone: 'iPhone',
      'android-phone': 'Android Phone',
      'standard-tablet': 'Standard Tablet',
      'drawing-tablet': 'Drawing Tablet',
      'e-reader': 'E-Reader',
      'fitness-band': 'Fitness Band',
      headphones: 'Headphones',
      earbuds: 'Earbuds',
      speaker: 'Speaker',
      'hifi-component': 'Hi-Fi Component',
      'camera-body': 'Camera Body',
      'camera-lens': 'Camera Lens',
      'action-camera': 'Action Camera',
      camcorder: 'Camcorder',
      'camera-accessory': 'Camera Accessory',
      projector: 'Projector',
      'home-cinema': 'Home Cinema',
      playstation: 'PlayStation',
      xbox: 'Xbox',
      nintendo: 'Nintendo',
      'handheld-console': 'Handheld Console',
      'gaming-accessory': 'Gaming Accessory',
      'mobile-accessory': 'Mobile Accessory',
      'phone-case': 'Phone Case',
      'charger-cable': 'Charger & Cable',
      'screen-protector': 'Screen Protector',
      'wearable-accessory': 'Wearable Accessory',
      'smart-home-device': 'Smart Home Device',
      'smart-speaker': 'Smart Speaker',
      'security-camera': 'Security Camera',
      'smart-lighting': 'Smart Lighting',
      'smart-thermostat': 'Smart Thermostat',
      macbook: 'MacBook',
      'windows-laptop': 'Windows Laptop',
      chromebook: 'Chromebook',
      'desktop-tower': 'Desktop Tower',
      'all-in-one': 'All-in-One',
      'mini-pc': 'Mini PC',
      'computer-monitor': 'Computer Monitor',
      'portable-monitor': 'Portable Monitor',
      'graphics-card': 'Graphics Card',
      processor: 'Processor',
      memory: 'Memory',
      'storage-drive': 'Storage Drive',
      motherboard: 'Motherboard',
      'power-supply': 'Power Supply',
      'pc-case': 'PC Case',
      router: 'Router',
      modem: 'Modem',
      'wifi-extender': 'Wi-Fi Extender',
      'network-switch': 'Network Switch',
      'inkjet-printer': 'Inkjet Printer',
      'laser-printer': 'Laser Printer',
      scanner: 'Scanner',
      '3d-printer': '3D Printer',
      keyboard: 'Keyboard',
      'mouse-trackpad': 'Mouse & Trackpad',
      'docking-station': 'Docking Station',
      'cable-adapter': 'Cable & Adapter',
      'computer-bag': 'Computer Bag',
      'coffee-machine': 'Coffee Machine',
      microwave: 'Microwave',
      toaster: 'Toaster',
      blender: 'Blender',
      kettle: 'Kettle',
      'food-processor': 'Food Processor',
      'air-fryer': 'Air Fryer',
      'refrigerator-freezer': 'Refrigerator & Freezer',
      dishwasher: 'Dishwasher',
      'oven-cooktop': 'Oven & Cooktop',
      'extractor-hood': 'Extractor Hood',
      'laundry-appliance': 'Laundry Appliance',
      'washing-machine': 'Washing Machine',
      'tumble-dryer': 'Tumble Dryer',
      'washer-dryer': 'Washer-Dryer',
      vacuum: 'Vacuum',
      'robot-vacuum': 'Robot Vacuum',
      'steam-cleaner': 'Steam Cleaner',
      heater: 'Heater',
      fan: 'Fan',
      'air-conditioner': 'Air Conditioner',
      'air-purifier': 'Air Purifier',
      dehumidifier: 'Dehumidifier',
      'hair-dryer': 'Hair Dryer',
      'hair-styler': 'Hair Styler',
      shaver: 'Shaver',
      'electric-toothbrush': 'Electric Toothbrush',
      armchair: 'Armchair',
      'dining-table': 'Dining Table',
      'coffee-table': 'Coffee Table',
      desk: 'Desk',
      'dining-chair': 'Dining Chair',
      'office-chair': 'Office Chair',
      stool: 'Stool',
      wardrobe: 'Wardrobe',
      dresser: 'Dresser',
      'bookcase-shelf': 'Bookcase & Shelf',
      cabinet: 'Cabinet',
      'bed-frame': 'Bed Frame',
      'bedside-table': 'Bedside Table',
      mattress: 'Mattress',
      'adult-mattress': 'Adult Mattress',
      'child-mattress': 'Child Mattress',
      'mattress-topper': 'Mattress Topper',
      'office-desk': 'Office Desk',
      'garden-table-chair': 'Garden Table & Chair',
      'outdoor-storage': 'Outdoor Storage',
      lamp: 'Lamp',
      'ceiling-light': 'Ceiling Light',
      bulb: 'Bulb',
      'light-fixture': 'Light Fixture',
      'household-linen': 'Household Linen',
      'rug-carpet': 'Rug & Carpet',
      curtains: 'Curtains',
      'cushion-pillow': 'Cushion & Pillow',
      'blanket-duvet': 'Blanket & Duvet',
      'print-poster': 'Print & Poster',
      painting: 'Painting',
      'mirror-frame': 'Mirror & Frame',
      tableware: 'Tableware',
      cookware: 'Cookware',
      'kitchen-tool': 'Kitchen Tool',
      cutlery: 'Cutlery',
      'plant-pot': 'Plant Pot',
      planter: 'Planter',
      'garden-decoration': 'Garden Decoration',
      't-shirt-top': 'T-Shirt & Top',
      'shirt-blouse': 'Shirt & Blouse',
      'jeans-trousers': 'Jeans & Trousers',
      'dress-skirt': 'Dress & Skirt',
      outerwear: 'Outerwear',
      knitwear: 'Knitwear',
      sportswear: 'Sportswear',
      'suit-formal': 'Suit & Formalwear',
      shirt: 'Shirt',
      sneakers: 'Sneakers',
      boots: 'Boots',
      'formal-shoes': 'Formal Shoes',
      sandals: 'Sandals',
      handbag: 'Handbag',
      backpack: 'Backpack',
      luggage: 'Luggage',
      'wallet-small-leather': 'Wallet & Small Leather Goods',
      watch: 'Watch',
      jewelry: 'Jewelry',
      'costume-jewelry': 'Costume Jewelry',
      sunglasses: 'Sunglasses',
      belt: 'Belt',
      scarf: 'Scarf',
      hat: 'Hat',
      tie: 'Tie',
      'standard-stroller': 'Standard Stroller',
      'travel-stroller': 'Travel Stroller',
      'double-stroller': 'Double Stroller',
      'infant-car-seat': 'Infant Car Seat',
      'child-car-seat': 'Child Car Seat',
      'booster-seat': 'Booster Seat',
      carrier: 'Carrier',
      'crib-cot': 'Crib & Cot',
      'high-chair': 'High Chair',
      'baby-monitor': 'Baby Monitor',
      'feeding-gear': 'Feeding Gear',
      'baby-bath-changing': 'Baby Bath & Changing',
      'baby-clothing': 'Baby Clothing',
      'child-clothing': 'Child Clothing',
      'kids-footwear': 'Kids Footwear',
      'baby-toy': 'Baby Toy',
      'maternity-clothing': 'Maternity Clothing',
      'nursing-gear': 'Nursing Gear',
      'child-furniture': 'Child Furniture',
      'kids-bed': 'Kids Bed',
      'kids-mattress': 'Kids Mattress',
      'nursery-storage': 'Nursery Storage',
      'kids-desk-chair': 'Kids Desk & Chair',
      weights: 'Weights',
      'cardio-machine': 'Cardio Machine',
      'yoga-pilates': 'Yoga & Pilates',
      'boxing-mma': 'Boxing & MMA',
      'bicycle-accessory': 'Bicycle Accessory',
      helmet: 'Helmet',
      'child-seat-trailer': 'Child Seat & Trailer',
      'bike-part': 'Bike Part',
      'bike-rack-bag': 'Bike Rack & Bag',
      'e-scooter-mobility': 'E-Scooter & Mobility',
      'e-scooter': 'E-Scooter',
      'kick-scooter': 'Kick Scooter',
      'mobility-scooter': 'Mobility Scooter',
      'running-shoes': 'Running Shoes',
      'gps-watch': 'GPS Watch',
      'running-accessory': 'Running Accessory',
      football: 'Football',
      basketball: 'Basketball',
      rugby: 'Rugby',
      'protection-gear': 'Protection Gear',
      tent: 'Tent',
      'sleeping-bag': 'Sleeping Bag',
      'hiking-poles': 'Hiking Poles',
      surfboard: 'Surfboard',
      paddleboard: 'Paddleboard',
      wetsuit: 'Wetsuit',
      'diving-gear': 'Diving Gear',
      'racket-sport-gear': 'Racket Sport Gear',
      tennis: 'Tennis',
      badminton: 'Badminton',
      padel: 'Padel',
      'table-tennis': 'Table Tennis',
      'winter-sport-gear': 'Winter Sport Gear',
      skis: 'Skis',
      snowboard: 'Snowboard',
      'winter-boots': 'Winter Boots',
      'fishing-gear': 'Fishing Gear',
      'fishing-rod': 'Fishing Rod',
      reel: 'Reel',
      'tackle-box': 'Tackle Box',
      'equestrian-gear': 'Equestrian Gear',
      saddle: 'Saddle',
      'riding-clothing': 'Riding Clothing',
      'horse-tack': 'Horse Tack',
      'drill-driver': 'Drill & Driver',
      saw: 'Saw',
      'sander-grinder': 'Sander & Grinder',
      'battery-charger': 'Battery & Charger',
      'electric-garden-tool': 'Electric Garden Tool',
      hammer: 'Hammer',
      'screwdriver-wrench': 'Screwdriver & Wrench',
      'hand-saw': 'Hand Saw',
      'measuring-tool': 'Measuring Tool',
      ladder: 'Ladder',
      workbench: 'Workbench',
      compressor: 'Compressor',
      'welding-tool': 'Welding Tool',
      'wood-timber': 'Wood & Timber',
      'metal-material': 'Metal Material',
      'tiles-ceramic': 'Tiles & Ceramic',
      'plumbing-fixture': 'Plumbing Fixture',
      'door-window': 'Door & Window',
      insulation: 'Insulation',
      'lawn-mower': 'Lawn Mower',
      'hedge-trimmer': 'Hedge Trimmer',
      'garden-hand-tool': 'Garden Hand Tool',
      'watering-equipment': 'Watering Equipment',
      'body-part': 'Body Part',
      'mechanical-part': 'Mechanical Part',
      'moto-body-part': 'Moto Body Part',
      'moto-mechanical-part': 'Moto Mechanical Part',
      tire: 'Tire',
      'wheel-rim': 'Wheel & Rim',
      'car-audio': 'Car Audio',
      'gps-dashcam': 'GPS & Dashcam',
      'roof-rack': 'Roof Rack',
      'car-seat-cover': 'Car Seat Cover',
      'motorcycle-gear': 'Motorcycle Gear',
      'novel-literature': 'Novel & Literature',
      textbook: 'Textbook',
      'children-book': 'Children Book',
      'comic-book': 'Comic Book',
      manga: 'Manga',
      'art-book': 'Art Book',
      comic: 'Comic',
      magazine: 'Magazine',
      'switch-game': 'Switch Game',
      'playstation-game': 'PlayStation Game',
      'xbox-game': 'Xbox Game',
      'pc-game': 'PC Game',
      'construction-set': 'Construction Set',
      lego: 'LEGO',
      duplo: 'Duplo',
      'compatible-bricks': 'Compatible Bricks',
      'doll-plush': 'Doll & Plush',
      'model-figure': 'Model Figure',
      'science-kit': 'Science Kit',
      'craft-kit': 'Craft Kit',
      'electronic-toy': 'Electronic Toy',
      'remote-control-toy': 'Remote Control Toy',
      'battery-toy': 'Battery Toy',
      'electronic-learning-toy': 'Electronic Learning Toy',
      'outdoor-toy': 'Outdoor Toy',
      'ride-on-toy': 'Ride-On Toy',
      'playground-toy': 'Playground Toy',
      'antique-furniture': 'Antique Furniture',
      'antique-decor': 'Antique Decor',
      coin: 'Coin',
      banknote: 'Banknote',
      stamp: 'Stamp',
      postcard: 'Postcard',
      'tcg-card': 'TCG Card',
      'sports-card': 'Sports Card',
      sculpture: 'Sculpture',
      print: 'Print',
      memorabilia: 'Memorabilia',
      'advertising-collectible': 'Advertising Collectible',
      militaria: 'Militaria',
      'model-kit': 'Model Kit',
      'tabletop-miniature': 'Tabletop Miniature',
      'acoustic-guitar': 'Acoustic Guitar',
      'electric-guitar': 'Electric Guitar',
      'bass-guitar': 'Bass Guitar',
      'digital-piano': 'Digital Piano',
      'acoustic-piano': 'Acoustic Piano',
      'drum-kit': 'Drum Kit',
      cymbal: 'Cymbal',
      percussion: 'Percussion',
      turntable: 'Turntable',
      mixer: 'Mixer',
      controller: 'Controller',
      microphone: 'Microphone',
      'audio-interface': 'Audio Interface',
      'studio-monitor': 'Studio Monitor',
      violin: 'Violin',
      'wind-instrument': 'Wind Instrument',
      'brass-instrument': 'Brass Instrument',
      'sealed-skincare': 'Sealed Skincare',
      'sealed-makeup': 'Sealed Makeup',
      'sealed-haircare': 'Sealed Haircare',
      'massage-device': 'Massage Device',
      'fitness-health-tracker': 'Fitness Health Tracker',
      'mobility-aid': 'Mobility Aid',
      'medical-device': 'Medical Device',
      'medical-consumable': 'Medical Consumable',
      'pet-bed': 'Pet Bed',
      'pet-carrier': 'Pet Carrier',
      'pet-toy': 'Pet Toy',
      'leash-collar': 'Leash & Collar',
      'pet-bowl': 'Pet Bowl',
      'aquarium-terrarium-equipment': 'Aquarium & Terrarium Equipment',
      'plant-cutting': 'Plant Cutting',
      'potted-plant': 'Potted Plant',
      'plant-care-supplies': 'Plant Care Supplies',
      'plant-care-tool': 'Plant Care Tool',
      'office-school': 'Office & School',
      stationery: 'Stationery',
      'notebooks-paper': 'Notebooks & Paper',
      'writing-supplies': 'Writing Supplies',
      'school-supplies': 'School Supplies',
      calculator: 'Calculator',
      'art-supplies': 'Art Supplies',
      'office-equipment': 'Office Equipment',
      shredder: 'Shredder',
      laminator: 'Laminator',
      'label-maker': 'Label Maker',
      'office-phone': 'Office Phone',
      'office-supplies': 'Office Supplies',
      'filing-storage': 'Filing & Storage',
      'desk-accessory': 'Desk Accessory',
      'printer-consumable': 'Printer Consumable',
      'restricted-good': 'Restricted Good',
      'food-beverage': 'Food & Beverage',
      'live-animal': 'Live Animal',
      'hazardous-material': 'Hazardous Material',
      'service-listing': 'Service Listing',
      vehicle: 'Vehicle',
      'real-estate': 'Real Estate',
      other: 'Other',
      'general-item': 'General Item',
    },
  },
  nativeUpdate: {
    title: 'Update required',
    body: 'This version of Liberio is too old to continue. Update it to get back to your items.',
    cta: 'Update',
    storeHint: 'Open the App Store to install the latest version.',
    storeHintAndroid: 'Open the Play Store to install the latest version.',
    reminderTitle: 'A new version of Liberio is here',
    reminderBody: 'It is waiting for you on the App Store, with the latest improvements. It takes a minute.',
    reminderBodyAndroid: 'It is waiting for you on the Play Store, with the latest improvements. It takes a minute.',
    reminderCta: 'Update',
    reminderLater: 'Later',
  },
  nativeBack: {
    exitConfirm: 'Press back again to leave Liberio',
  },
  tabs: {
    openScanner: 'Open scanner',
    scan: 'Scan',
    items: 'Items',
    settings: 'Settings',
    shop: 'My shop',
  },
  map: {
    you: 'You',
    searchInZone: 'Search in this zone',
    backToMyPlace: 'Back to my place',
  },
  motivation: {
    title: 'Thanks, noted',
    body: 'It helps us improve so we can be even more useful to you!',
    scan: 'Scan an item',
  },
  ouvrir: {
    redirecting: 'Taking you to the store…',
  },
} as const;

export type CoreMessages = WidenMessageLeaves<typeof enCoreMessages>;
